import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { TextField } from "@/components/Form";

/** Names the picker shows for what is typed: every one containing it (ignoring
 * case), the ones that *start* with it first. */
export function filterAttractions(all: string[], query: string, limit = 40): string[] {
  const q = query.trim().toLocaleLowerCase();
  if (!q) return all.slice(0, limit);
  const hits = all.filter((n) => n.toLocaleLowerCase().includes(q));
  const starts = hits.filter((n) => n.toLocaleLowerCase().startsWith(q));
  const rest = hits.filter((n) => !n.toLocaleLowerCase().startsWith(q));
  return [...starts, ...rest].slice(0, limit);
}

/** «Парк / аттракцион» of a park-tickets line: a text field with a searchable
 * list under it. Picking fills the field; anything typed that is not on the list
 * is kept as is — and, once the line is saved, the server offers it next time
 * (`GET /bookings/ticket-attractions` = the usual parks + every name used). */
export function AttractionPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const names = useQuery({
    queryKey: ["ticket-attractions"],
    queryFn: () => api.get<string[]>("/bookings/ticket-attractions"),
    staleTime: 5 * 60_000,
  });
  const all = names.data ?? [];
  const shown = filterAttractions(all, value);
  const typed = value.trim();
  // A name that matches nothing is new — it is kept as typed (said only then, not
  // while the list still offers matches).
  const isNew = typed !== "" && shown.length === 0;

  return (
    <div className="relative">
      <TextField
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        placeholder={t("bookingLines.attractionPlaceholder")}
        value={value}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "Enter" && open) {
            // Enter takes the first match; a new name stays as typed.
            e.preventDefault();
            if (shown.length > 0 && !isNew) onChange(shown[0]);
            setOpen(false);
          }
        }}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
      />
      {open && (shown.length > 0 || isNew) && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-y-auto rounded-[10px] border-[1.5px] border-[#2A9D8F] bg-white py-1 shadow-lg"
        >
          {isNew && (
            <li className="px-3 py-2 text-xs text-ink-muted">
              {t("bookingLines.attractionNew", { name: typed })}
            </li>
          )}
          {shown.map((name) => (
            <li key={name}>
              <button
                type="button"
                role="option"
                aria-selected={name === value}
                className="block w-full px-3 py-2 text-left text-sm text-ink hover:bg-[#E0F2EF]"
                // mousedown, not click: the field's blur would close the list first
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(name);
                  setOpen(false);
                }}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
